import { useState, useRef, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { X, UserPlus, Camera, Sparkles, Send, Loader2 } from "lucide-react";
import { BELT_NAMES } from "@/lib/belts";
import { useT } from "@/context/LanguageContext";
import PhotoCaptureModal from "@/components/PhotoCaptureModal";

/**
 * Manual user-create modal. Replaces access-code registration for staff:
 * admins fill in info, click Create. Username auto-generates (yoshi-userNN)
 * and password is left blank — student/team accounts are roster-only and
 * cannot log in. Only staff (admin/super_admin) accounts get credentials
 * (assigned later via password reset).
 */
export default function AddUserModal({ currentUser, onClose, onCreated }) {
  const { lang } = useT();
  const isSuper = currentUser?.role === "super_admin";
  const [draft, setDraft] = useState({
    name: "", email: "", role: "student",
    phone: "", belt_rank: "White",
    date_of_birth: "", address: "",
    emergency_contact_name: "", emergency_contact_phone: "",
    medical_notes: "", notes: "", photo_url: "",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [photoModal, setPhotoModal] = useState(false);
  // Captured `photo_size` override — only PATCH'd after create when ≠ 1.0
  const [photoSize, setPhotoSize] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [cardTemplate, setCardTemplate] = useState("");

  useEffect(() => {
    api.get("/idcard-templates").then((r) => setTemplates(r.data || [])).catch(() => {});
  }, []);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  // ── AI assistant state ──────────────────────────────────────────────────
  const AI_STRINGS = {
    en: {
      title: "AI Assistant",
      subtitle: "Describe the new member in plain language — I'll fill the form.",
      greeting: "Hi! Tell me about the new member — their name, role (student/staff), belt, phone, date of birth, and anything else. I'll fill out the form for you.",
      placeholder: "e.g. New student Maria Lopez, white belt, phone 708-555-1234…",
      applied: "Applied to form",
      fieldNames: { name: "Name", email: "Email", role: "Role", phone: "Phone", belt_rank: "Belt", date_of_birth: "Date of birth", address: "Address", emergency_contact_name: "Emergency contact", emergency_contact_phone: "Emergency phone", medical_notes: "Medical notes", notes: "Notes" },
      ready: "All set — review the form below and click Create User.",
      error: "The assistant is unavailable right now. You can fill the form manually.",
      notConfigured: "The AI assistant isn't set up on this server yet (missing AI key). You can fill the form manually.",
      busy: "The AI service didn't answer in time. Please send your message again.",
      keyRejected: "The AI key saved on this server was rejected. To fix it: run the update command on the server and paste your Emergent Universal Key (it starts with sk-emergent-). You can fill the form manually for now.",
      send: "Send",
    },
    es: {
      title: "Asistente de IA",
      subtitle: "Describe al nuevo miembro en lenguaje natural — yo lleno el formulario.",
      greeting: "¡Hola! Cuéntame sobre el nuevo miembro — su nombre, rol (estudiante/personal), cinturón, teléfono, fecha de nacimiento y lo que sea. Yo lleno el formulario por ti.",
      placeholder: "ej. Nueva estudiante Maria Lopez, cinturón blanco, teléfono 708-555-1234…",
      applied: "Aplicado al formulario",
      fieldNames: { name: "Nombre", email: "Correo", role: "Rol", phone: "Teléfono", belt_rank: "Cinturón", date_of_birth: "Fecha de nacimiento", address: "Dirección", emergency_contact_name: "Contacto de emergencia", emergency_contact_phone: "Teléfono de emergencia", medical_notes: "Notas médicas", notes: "Notas" },
      ready: "¡Listo! Revisa el formulario y haz clic en Crear Usuario.",
      error: "El asistente no está disponible ahora. Puedes llenar el formulario manualmente.",
      notConfigured: "El asistente de IA aún no está configurado en este servidor (falta la clave de IA). Puedes llenar el formulario manualmente.",
      busy: "El servicio de IA no respondió a tiempo. Por favor envía tu mensaje otra vez.",
      keyRejected: "La clave de IA guardada en este servidor fue rechazada. Para arreglarlo: ejecuta el comando de actualización en el servidor y pega tu Emergent Universal Key (empieza con sk-emergent-). Por ahora puedes llenar el formulario manualmente.",
      send: "Enviar",
    },
  };
  const S = AI_STRINGS[lang === "es" ? "es" : "en"];
  const [aiOpen, setAiOpen] = useState(true);
  const [aiMessages, setAiMessages] = useState([{ role: "assistant", content: S.greeting }]);
  const [aiInput, setAiInput] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiReady, setAiReady] = useState(false);
  const sessionIdRef = useRef(`intake-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const scrollRef = useRef(null);

  // Reset greeting when language changes (only if conversation not started).
  useEffect(() => {
    setAiMessages((prev) => (prev.length <= 1 ? [{ role: "assistant", content: S.greeting }] : prev));
  }, [lang]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [aiMessages, aiBusy]);

  const applyFields = (fields) => {
    if (!fields || typeof fields !== "object") return [];
    const applied = [];
    setDraft((d) => {
      const next = { ...d };
      Object.entries(fields).forEach(([k, v]) => {
        if (v == null || `${v}`.trim() === "") return;
        if (k in next) {
          next[k] = v;
          applied.push(k);
        }
      });
      return next;
    });
    return Object.keys(fields);
  };

  const sendAi = async () => {
    const text = aiInput.trim();
    if (!text || aiBusy) return;
    const newMsgs = [...aiMessages, { role: "admin", content: text }];
    setAiMessages(newMsgs);
    setAiInput("");
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/user-intake", {
        session_id: sessionIdRef.current,
        language: lang === "es" ? "es" : "en",
        messages: newMsgs.map((m) => ({ role: m.role, content: m.content })),
      }, { timeout: 70000, noRetry: true });
      const appliedKeys = applyFields(data.fields);
      setAiMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply || "…", applied: appliedKeys },
      ]);
      setAiReady(!!data.ready);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg = status === 503 ? S.notConfigured : !status ? S.busy : S.error;
      if (status === 503 && typeof detail === "string" && detail.startsWith("AI_KEY_REJECTED")) msg = S.keyRejected;
      // Show the server's real reason so it can be fixed.
      if (status === 502 && typeof detail === "string") msg = `${S.error} (${detail})`;
      setAiMessages((prev) => [...prev, { role: "assistant", content: msg }]);
    } finally {
      setAiBusy(false);
    }
  };

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1.2 * 1024 * 1024) { alert("Photo must be under 1.2 MB"); return; }
    const r = new FileReader();
    r.onload = () => set("photo_url", r.result);
    r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg("");
    try {
      // Build a normalised payload:
      //   • required fields (name, password, role) are coerced to strings so
      //     Pydantic can't fail on `null`;
      //   • optional string fields are trimmed and sent as `null` if empty so
      //     EmailStr/typed validators aren't handed `""`.
      const clean = (v) => {
        const s = (v ?? "").toString().trim();
        return s === "" ? null : s;
      };
      const payload = {
        name: (draft.name ?? "").toString().trim(),
        email: clean(draft.email),
        role: draft.role || "student",
        phone: clean(draft.phone),
        belt_rank: clean(draft.belt_rank),
        date_of_birth: clean(draft.date_of_birth),
        address: clean(draft.address),
        emergency_contact_name: clean(draft.emergency_contact_name),
        emergency_contact_phone: clean(draft.emergency_contact_phone),
        medical_notes: clean(draft.medical_notes),
        notes: clean(draft.notes),
        photo_url: draft.photo_url || null,
      };
      if (!payload.name) {
        setMsg("Full name is required.");
        setBusy(false);
        return;
      }
      const { data } = await api.post("/users", payload);
      // Apply the chosen ID-card template + any photo sizing from the capture
      // modal to the freshly created account.
      const extra = {};
      // "" = Default (CMS) design — nothing to set.
      if (cardTemplate) extra.idcard_template = cardTemplate;
      if (photoSize && photoSize !== 1) {
        extra.idcard_overrides = { ...(data.idcard_overrides || {}), photo_size: photoSize };
      }
      let created = data;
      if (Object.keys(extra).length) {
        try {
          const r = await api.patch(`/users/${data.id}`, extra);
          created = r.data || data;
        } catch (_) { /* non-fatal — user is still created */ }
      }
      onCreated?.(created);
      onClose();
    } catch (err) {
      setMsg(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="add-user-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute inset-0 flex items-start justify-center p-6 overflow-y-auto">
        <form onSubmit={submit} className="relative bg-[var(--dojo-paper)] border border-[var(--dojo-border)] w-full max-w-3xl my-8">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--dojo-border)]">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)]">Onboarding</div>
              <h2 className="font-serif text-2xl">Add New User</h2>
            </div>
            <button type="button" onClick={onClose} className="p-2 hover:text-[var(--dojo-hinomaru)]"><X size={18} /></button>
          </div>
          <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* AI Assistant */}
            <div className="border border-[var(--dojo-maroon)] bg-[var(--dojo-maroon-soft)] rounded-sm overflow-hidden" data-testid="ai-assist-panel">
              <button
                type="button"
                onClick={() => setAiOpen((o) => !o)}
                className="w-full flex items-center justify-between px-4 py-3 text-left"
                data-testid="ai-assist-toggle"
              >
                <span className="flex items-center gap-2 font-medium text-[var(--dojo-maroon)]">
                  <Sparkles size={16} /> {S.title}
                </span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--dojo-ink-soft)]">{aiOpen ? "–" : "+"}</span>
              </button>
              {aiOpen && (
                <div className="px-4 pb-4">
                  <p className="text-xs text-[var(--dojo-ink-soft)] mb-3">{S.subtitle}</p>
                  <div ref={scrollRef} className="max-h-56 overflow-y-auto space-y-3 mb-3 pr-1" data-testid="ai-messages">
                    {aiMessages.map((m, i) => (
                      <div key={i} className={`flex ${m.role === "admin" ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[85%] px-3 py-2 text-sm rounded-lg ${
                            m.role === "admin"
                              ? "bg-[var(--dojo-maroon)] text-white"
                              : "bg-white border border-[var(--dojo-border)] text-[#1A1A1A]"
                          }`}
                        >
                          <div>{m.content}</div>
                          {m.applied && m.applied.length > 0 && (
                            <div className="mt-1.5 text-[10px] uppercase tracking-wide text-[var(--dojo-green)] font-medium">
                              ✓ {S.applied}: {m.applied.map((k) => S.fieldNames[k] || k).join(", ")}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {aiBusy && (
                      <div className="flex justify-start">
                        <div className="px-3 py-2 bg-white border border-[var(--dojo-border)] rounded-lg">
                          <Loader2 size={16} className="animate-spin text-[var(--dojo-maroon)]" />
                        </div>
                      </div>
                    )}
                  </div>
                  {aiReady && (
                    <div className="text-xs text-[var(--dojo-green)] font-medium mb-2" data-testid="ai-ready">✓ {S.ready}</div>
                  )}
                  <div className="flex gap-2">
                    <input
                      className="input flex-1"
                      value={aiInput}
                      onChange={(e) => setAiInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); sendAi(); } }}
                      placeholder={S.placeholder}
                      data-testid="ai-input"
                      disabled={aiBusy}
                    />
                    <button
                      type="button"
                      onClick={sendAi}
                      disabled={aiBusy || !aiInput.trim()}
                      className="btn-primary flex items-center gap-1.5 px-4"
                      data-testid="ai-send"
                    >
                      <Send size={14} /> {S.send}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <Section title="Account">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Full Name *"><input className="input" required value={draft.name} onChange={(e) => set("name", e.target.value)} data-testid="newuser-name" /></Field>
                <Field label="Email" hint="Optional. Useful for notifications.">
                  <input className="input" type="email" value={draft.email} onChange={(e) => set("email", e.target.value)} data-testid="newuser-email" placeholder="(optional)" />
                </Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Role">
                  <select className="input" value={draft.role} onChange={(e) => set("role", e.target.value)} data-testid="newuser-role">
                    <option value="student">Student</option>
                    <option value="team_member">Team Member</option>
                    <option value="sensei">Sensei</option>
                    <option value="renshi">Renshi</option>
                    {isSuper && <option value="admin">Admin</option>}
                    {isSuper && <option value="super_admin">Super Admin</option>}
                  </select>
                </Field>
                <Field label="Phone"><input className="input" value={draft.phone} onChange={(e) => set("phone", e.target.value)} data-testid="newuser-phone" /></Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Belt Rank">
                  <select className="input" value={draft.belt_rank} onChange={(e) => set("belt_rank", e.target.value)} data-testid="newuser-belt">
                    {BELT_NAMES.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </Field>
                <Field label="ID Card Template" hint="Pick a saved design — edit the designs in ID Card Templates.">
                  <select
                    className="input"
                    value={cardTemplate}
                    onChange={(e) => setCardTemplate(e.target.value)}
                    data-testid="newuser-idcard-template"
                  >
                    <option value="">— Default (CMS) —</option>
                    {templates.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </Field>
              </div>
            </Section>

            <Section title="Information">
              <Field label="Date of Birth"><input className="input" type="date" value={draft.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} /></Field>
              <Field label="Address"><textarea className="input min-h-[60px]" value={draft.address} onChange={(e) => set("address", e.target.value)} /></Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Emergency Contact Name"><input className="input" value={draft.emergency_contact_name} onChange={(e) => set("emergency_contact_name", e.target.value)} /></Field>
                <Field label="Emergency Contact Phone"><input className="input" value={draft.emergency_contact_phone} onChange={(e) => set("emergency_contact_phone", e.target.value)} /></Field>
              </div>
              <Field label="Medical Notes"><textarea className="input min-h-[60px]" value={draft.medical_notes} onChange={(e) => set("medical_notes", e.target.value)} placeholder="Allergies, injuries…" /></Field>
              <Field label="Internal Notes"><textarea className="input min-h-[60px]" value={draft.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
              <Field label="Photo" hint="Optional. Use Capture for webcam + crop, or pick a file.">
                <div className="flex items-center gap-3 flex-wrap">
                  {draft.photo_url && <img src={draft.photo_url} alt="" className="h-16 w-[52px] object-cover border border-[var(--dojo-border)]" data-testid="newuser-photo-preview" />}
                  <button
                    type="button"
                    onClick={() => setPhotoModal(true)}
                    className="btn-outline flex items-center gap-2 text-xs"
                    data-testid="newuser-photo-capture-btn"
                  ><Camera size={13} /> Capture / Crop</button>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--dojo-ink-soft)]">or</span>
                  <input type="file" accept="image/*" onChange={onPhoto} className="text-sm" data-testid="newuser-photo-file" />
                  {draft.photo_url && (
                    <button type="button" onClick={() => { set("photo_url", ""); setPhotoSize(1); }} className="text-xs text-[var(--dojo-hinomaru)] underline">Remove</button>
                  )}
                </div>
              </Field>
            </Section>
          </div>
          {msg && <div className="px-6 pb-3 text-[var(--dojo-hinomaru)] text-sm" data-testid="newuser-error">{msg}</div>}
          <div className="px-6 py-4 border-t border-[var(--dojo-border)] flex justify-end gap-3">
            <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary flex items-center gap-2" data-testid="newuser-submit">
              <UserPlus size={14} /> {busy ? "Creating…" : "Create User"}
            </button>
          </div>
        </form>
      </div>
      {photoModal && (
        <PhotoCaptureModal
          initialPhotoSize={photoSize}
          onClose={() => setPhotoModal(false)}
          onConfirm={(dataUrl, size) => {
            set("photo_url", dataUrl);
            setPhotoSize(size || 1);
            setPhotoModal(false);
          }}
        />
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-4">
      <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] pb-2 border-b border-[var(--dojo-border)]">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.24em] text-[var(--dojo-ink-soft)] block mb-1.5">{label}</label>
      {children}
      {hint && <div className="text-[10px] text-[var(--dojo-ink-soft)] mt-1">{hint}</div>}
    </div>
  );
}
